import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphServices as ParagraphServicesData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphServices.module.css";

/**
 * Layout 45 - `PARAGRAPH_SERVICES_LAYOUT`: usługi (light full-bleed mint section).
 *
 * Struktura:
 *   - Light full-bleed mint bg
 *   - Header 2-kol:
 *       Lewa: eyebrow (text_1) + H2 (name) + lead (text, HTML)
 *       Prawa: pill CTA "Wszystkie uslugi" (button_name + button_url) - opcjonalny
 *   - Grid 3-kol z 6 kart:
 *       Ikona (Nucleo) w mint-pill boxie
 *       Nazwa (H3) + Opis (paragraph)
 *       Link "Dowiedz sie wiecej" (opcjonalny, gdy href niepusty)
 *   - Puste sloty filtrowane po backendzie
 */
export function ParagraphServices({
  paragraph,
  t,
}: {
  paragraph: ParagraphServicesData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: lead,
    button_name: ctaLabel,
    button_url: ctaUrl,
    items,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasCta = ctaLabel && ctaLabel.trim() !== "" && ctaUrl && ctaUrl.trim() !== "";
  const hasItems = Array.isArray(items) && items.length > 0;

  const ctaHref = hasCta ? extractInternalHref(ctaUrl) : null;

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
            {title ? <h2 className={styles.title}>{title}</h2> : null}
            {hasLead ? (
              <HtmlText html={lead} className={styles.lead} />
            ) : null}
          </div>
          {hasCta ? (
            ctaHref !== null ? (
              <Link href={ctaHref} className={styles.ctaBtn}>
                {ctaLabel}
              </Link>
            ) : (
              <a href={ctaUrl} className={styles.ctaBtn}>
                {ctaLabel}
              </a>
            )
          ) : null}
        </div>

        {hasItems ? (
          <div className={styles.grid}>
            {items.map((it, idx) => {
              const itemHref = it.href && it.href.trim() !== "" ? extractInternalHref(it.href) : null;
              const cardInner = (
                <>
                  {it.icon ? (
                    <div className={styles.iconBox} aria-hidden="true">
                      <i className={`nc-icon-outline ${it.icon}`} />
                    </div>
                  ) : null}
                  {it.name ? <h3 className={styles.name}>{it.name}</h3> : null}
                  {it.desc ? <p className={styles.desc}>{it.desc}</p> : null}
                  {it.href && it.href.trim() !== "" ? (
                    <span className={styles.more}>
                      {t.services?.readMore ?? "Dowiedz się więcej"}
                    </span>
                  ) : null}
                </>
              );

              if (it.href && it.href.trim() !== "") {
                return itemHref !== null ? (
                  <Link key={idx} href={itemHref} className={styles.card}>
                    {cardInner}
                  </Link>
                ) : (
                  <a key={idx} href={it.href} className={styles.card}>
                    {cardInner}
                  </a>
                );
              }
              return (
                <div key={idx} className={styles.card}>
                  {cardInner}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
